import { Head, Link } from "@inertiajs/react";
import { ArrowRight } from "lucide-react";

import { ActivationGuide } from "@/components/activation-guide";
import PublicHeader from "@/components/public-header";
import { SiteFooter } from "@/components/site-footer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

interface RailsOgFaq {
  question: string;
  answer: string;
}

interface RailsOgImageProps {
  page_title?: string;
  faqs?: RailsOgFaq[];
}

interface ApproachRow {
  approach: string;
  setup: string;
  costLatency: string;
  caching: string;
  maintenance: string;
  dynamic: string;
}

const ROWS: ApproachRow[] = [
  {
    approach: "Static file in the layout",
    setup: "One file in public/ and one meta tag in the layout.",
    costLatency:
      "No render on the request. The server sends a file that already exists. This page does not publish a millisecond figure.",
    caching:
      "The file stays until you replace it. Social platforms cache by URL, so a new filename is what makes them fetch again.",
    maintenance: "You replace the artwork by hand. Every URL shares it unless you add more files.",
    dynamic: "No. The same pixels for every post, product, or profile.",
  },
  {
    approach: "Your renderer + Active Storage",
    setup:
      "A has_one_attached, a job, and an image library or a headless browser you operate. This page does not name a rendering gem.",
    costLatency:
      "The HTML request can serve the stored file. The render runs in the job. This page does not publish that render's time or host cost.",
    caching:
      "You replace the blob when the card fields change. Crawlers keep the previous preview until og:image points at a new URL and they fetch it.",
    maintenance: "You own fonts, layout, emoji, and the process that draws the file.",
    dynamic: "Yes, when the job reads the record and draws those fields.",
  },
  {
    approach: "OG Pilot API + Active Storage",
    setup:
      "The og_pilot_ruby gem, an API key, a verified domain, and a job that downloads the JSON url and attaches the bytes.",
    costLatency:
      "The HTML request serves the stored file. The API call runs in the job. The plan catalog lists Starter at $5/month ($50/year, 10,000-image quota), Builder at $49/month ($99/year, 100,000-image quota), and Growth at $99/month ($199/year, unlimited image quota). A Stripe image_quota feature can replace the quota. The catalog has no per-image price.",
    caching:
      "Replace the attachment when the card fields change. Pass updated_at as iat. The API docs say a present iat keeps the hosted cache for 24 hours from that timestamp. Omit iat and the hosted cache does not expire on its own.",
    maintenance: "OG Pilot owns the template. You own the job, the attachment, and the public URL.",
    dynamic:
      "Yes. Pass template, title, description, and path. Templates are listed on /docs, including blog_post and product.",
  },
];

const COLUMNS: { key: keyof Omit<ApproachRow, "approach">; label: string }[] = [
  { key: "setup", label: "Setup effort" },
  { key: "costLatency", label: "Per-request cost / latency" },
  { key: "caching", label: "Caching" },
  { key: "maintenance", label: "Maintenance" },
  { key: "dynamic", label: "Dynamic data" },
];

const STATIC_SAMPLE = `<%# public/social-card.jpg — one file for the whole site %>
<%= tag.meta property: "og:image", content: URI.join(root_url, "/social-card.jpg").to_s %>
<%= tag.meta property: "og:title", content: "Your site" %>
<%= tag.meta property: "og:description", content: "One description for every URL" %>
<%= tag.meta property: "og:url", content: request.original_url %>
<%= tag.meta name: "twitter:card", content: "summary_large_image" %>`;

const LAYOUT_SAMPLE = `# app/helpers/open_graph_helper.rb
module OpenGraphHelper
  def open_graph_tags(title:, description:, url:, image: nil, width: nil, height: nil)
    tags = []
    tags << tag.meta(property: "og:title", content: title)
    tags << tag.meta(property: "og:description", content: description)
    tags << tag.meta(property: "og:url", content: url)
    tags << tag.meta(property: "og:image", content: image) if image.present?
    tags << tag.meta(property: "og:image:width", content: width) if width.present?
    tags << tag.meta(property: "og:image:height", content: height) if height.present?
    tags << tag.meta(name: "twitter:card", content: "summary_large_image")
    safe_join(tags)
  end
end

# app/views/layouts/application.html.erb, inside <head>
<%= yield :head %>

# app/views/posts/show.html.erb
<% content_for :head do %>
  <%= open_graph_tags(
        title: @post.title,
        description: @post.excerpt,
        url: post_url(@post),
        image: (@post.og_image.attached? ? rails_blob_url(@post.og_image) : nil),
        width: 1200,
        height: 630
      ) %>
<% end %>`;

const JOB_SAMPLE = `# Gemfile — version from /docs
gem "og_pilot_ruby", "~> 0.4.1"

# bundle install
# bin/rails g og_pilot_ruby:install
# Set OG_PILOT_API_KEY and OG_PILOT_DOMAIN on the server.
# The gem signs the JWT. Do not add a hand-rolled signer.

# app/models/post.rb
class Post < ApplicationRecord
  has_one_attached :og_image

  after_commit :refresh_og_image_later, if: :og_card_fields_changed?

  private

  def og_card_fields_changed?
    previously_new_record? || saved_change_to_title? || saved_change_to_excerpt?
  end

  def refresh_og_image_later
    RefreshPostOgImageJob.perform_later(self)
  end
end

# app/jobs/refresh_post_og_image_job.rb
class RefreshPostOgImageJob < ApplicationJob
  queue_as :default

  def perform(post)
    Posts::OgImage.attach(post)
  end
end

# app/services/posts/og_image.rb
require "net/http"
require "stringio"
require "uri"

module Posts
  class OgImage
    def self.attach(post)
      new(post).attach
    end

    def initialize(post)
      @post = post
    end

    def attach
      metadata = OgPilotRuby.create_blog_post_image(
        title: post.title,
        description: post.excerpt.to_s,
        author_name: post.author&.name,
        publish_date: post.published_at&.to_date&.iso8601,
        path: "/posts/#{post.to_param}",
        image_type: "jpeg",
        iat: post.updated_at.to_i,
        json: true
      )
      return unless metadata.is_a?(Hash)
      return unless metadata["status"] == "completed"
      return if metadata["url"].blank?

      bytes = download(metadata["url"])
      return if bytes.blank?

      post.og_image.attach(
        io: StringIO.new(bytes),
        filename: "og-#{post.id}.jpg",
        content_type: "image/jpeg"
      )
    end

    private

    attr_reader :post

    def download(url, redirects = 3)
      uri = URI.parse(url)
      return unless uri.is_a?(URI::HTTPS)

      response = Net::HTTP.start(uri.host, uri.port, use_ssl: true) do |http|
        http.get(uri.request_uri)
      end
      if response.is_a?(Net::HTTPRedirection) && redirects.positive? && response["location"].present?
        return download(URI.join(uri, response["location"]).to_s, redirects - 1)
      end

      response.body if response.is_a?(Net::HTTPSuccess)
    end
  end
end`;

const MCP_SAMPLE = `{
  "mcpServers": {
    "og-pilot": {
      "type": "streamable-http",
      "url": "https://ogpilot.com/mcp"
    }
  }
}

# Auth: OAuth 2.1 with PKCE. No API key in this file.
# Docs: https://ogpilot.com/docs/mcp

generate_og_image
{ "url": "https://example.com/posts/your-post" }

# Optional: "style". Omitted or unknown uses auto-detect.
# Returns status, image_url, page_url, title, style.
# Does not attach Active Storage.`;

const sectionClass =
  "mt-16 rounded-3xl border border-orange-200/80 bg-white/90 p-6 shadow-xl sm:p-8 dark:border-orange-500/25 dark:bg-slate-900/80 dark:shadow-none";

const linkClass = "font-medium text-orange-700 hover:underline dark:text-orange-300";

function CodeSample({ label, code }: { label: string; code: string }) {
  return (
    <div className="mt-6">
      <p className="mb-2 text-sm font-semibold text-slate-900 dark:text-slate-100">{label}</p>
      <pre className="overflow-x-auto rounded-2xl border border-orange-200 bg-orange-50 p-5 text-sm leading-relaxed text-slate-900 dark:border-orange-500/30 dark:bg-slate-950 dark:text-orange-50">
        <code>{code}</code>
      </pre>
    </div>
  );
}

export default function RailsOgImage({
  page_title: pageTitle = "Rails OG Image: Active Storage vs API",
  faqs = [],
}: RailsOgImageProps) {
  return (
    <>
      <Head title={pageTitle} />

      <div className="min-h-screen bg-[radial-gradient(circle_at_0%_10%,#fed7aa_0,transparent_45%),radial-gradient(circle_at_100%_0%,#fecaca_0,transparent_38%),linear-gradient(180deg,#fff7ed_0%,#ffffff_32%,#fffbeb_100%)] pb-20 dark:bg-[radial-gradient(circle_at_0%_10%,#431407_0,transparent_45%),radial-gradient(circle_at_100%_0%,#7f1d1d_0,transparent_38%),linear-gradient(180deg,#0f172a_0%,#111827_32%,#0b1120_100%)]">
        <div className="container mx-auto max-w-6xl px-4 py-12">
          <PublicHeader />

          <section className="relative mt-8 overflow-hidden rounded-[2.25rem] border border-orange-200/80 bg-white/85 p-8 shadow-[0_35px_70px_-40px_rgba(194,65,12,0.55)] backdrop-blur sm:p-10 dark:border-orange-500/30 dark:bg-slate-900/80 dark:shadow-[0_35px_70px_-40px_rgba(249,115,22,0.35)]">
            <div className="relative">
              <Badge className="mb-5 border border-orange-200 bg-orange-100 px-4 py-1.5 tracking-[0.22em] text-orange-700 uppercase dark:border-orange-500/40 dark:bg-orange-500/20 dark:text-orange-100">
                rails og image
              </Badge>
              <h1 className="max-w-4xl text-4xl leading-tight font-bold text-balance text-slate-900 sm:text-5xl dark:text-slate-100">
                Rails OG image: static file, Active Storage, or the API
              </h1>
              <p className="mt-6 max-w-3xl text-base leading-relaxed text-slate-700 sm:text-lg dark:text-slate-300">
                Use this page when a Rails app needs an Open Graph image per record — a blog post, a product, a profile
                — and you do not want to design each card by hand. It compares three ways to do that, then gives a
                copy-paste job that calls the OG Pilot API and stores the file with Active Storage.
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
                <Button asChild size="lg" className="bg-slate-900 text-white hover:bg-slate-800">
                  <Link href="/sign_up">
                    Get an API key
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline">
                  <Link href="/docs/mcp">Install the MCP server</Link>
                </Button>
              </div>
            </div>
          </section>

          <section id="comparison" className={sectionClass}>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">Three ways to ship the card</h2>
            <div className="mt-4 max-w-3xl space-y-4 text-base leading-7 text-slate-700 dark:text-slate-300">
              <p>
                A static image in the layout is the right call when one artwork represents the whole site. Generating
                the file yourself and attaching it with Active Storage is the right call when each record needs its own
                pixels and you want to own the drawing code. Calling the hosted API and caching the result in Active
                Storage is the right call when each record needs its own card and you would rather not run an image
                library or a headless browser.
              </p>
              <p>
                There is a fourth path, already written up on the{" "}
                <Link href="/rails-og-image-generator" className={linkClass}>
                  Rails OG image guide
                </Link>
                . The gem returns a CDN URL on the request, and the layout prints that URL. You never store the bytes.
                Use that when a URL in the tag is enough. Use the job on this page when the file has to live on the
                record.
              </p>
            </div>
            <div className="mt-6 overflow-x-auto">
              <table className="w-full min-w-[72rem] border-collapse text-left text-sm text-slate-700 dark:text-slate-300">
                <thead>
                  <tr className="border-b border-orange-200 dark:border-orange-500/30">
                    <th className="px-3 py-3 font-semibold text-slate-900 dark:text-slate-100">Approach</th>
                    {COLUMNS.map((column) => (
                      <th key={column.key} className="px-3 py-3 font-semibold text-slate-900 dark:text-slate-100">
                        {column.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {ROWS.map((row) => (
                    <tr key={row.approach} className="border-b border-orange-100 align-top dark:border-orange-500/20">
                      <th className="px-3 py-3 font-semibold text-slate-900 dark:text-slate-100">{row.approach}</th>
                      {COLUMNS.map((column) => (
                        <td key={column.key} className="px-3 py-3">
                          {row[column.key]}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-4 text-sm leading-6 text-slate-600 dark:text-slate-300">
              API behavior is from <Link href="/docs">/docs</Link> and from{" "}
              <code>OgPilotRuby.create_blog_post_image</code> in the og_pilot_ruby gem: <code>json: true</code> returns
              the parsed JSON, and a request error returns a Hash with <code>image_url</code> set to nil. The success
              body in this app includes <code>status</code>, <code>url</code>, <code>width</code> 1200, and{" "}
              <code>height</code> 630. A render that is still running returns HTTP 202 with <code>status</code>{" "}
              <code>processing</code> or <code>failed</code>, <code>url</code> null, and <code>Retry-After: 30</code>.
            </p>
          </section>

          <section className={sectionClass}>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">Meta tags in the layout</h2>
            <div className="mt-4 max-w-3xl space-y-4 text-base leading-7 text-slate-700 dark:text-slate-300">
              <p>
                Put the tags in the layout head, filled from the record. <code>content_for :head</code> keeps the show
                template in charge of the values. <code>og:url</code> is the page, and <code>og:image</code> is the
                image. <code>twitter:card</code> is <code>summary_large_image</code> when you want the large card.
              </p>
              <p>
                A static file can live in <code>public/</code>. <code>root_url</code> makes the image URL absolute,
                which is what a crawler can fetch. Width and height below are the API response values, 1200 and 630.
                Pass the real dimensions of a file you drew yourself.
              </p>
              <p>
                <code>rails_blob_url</code> builds an absolute URL from the current request. In production the host has
                to be the public host. A disk service on localhost is not a URL a social crawler can fetch. Confirm the
                live URL in the{" "}
                <Link href="/open-graph-debugger" className={linkClass}>
                  Open Graph debugger
                </Link>
                .
              </p>
            </div>
            <CodeSample label="Static file" code={STATIC_SAMPLE} />
            <CodeSample label="Helper, layout, and the record" code={LAYOUT_SAMPLE} />
          </section>

          <section className={sectionClass}>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">Call the API and attach the file</h2>
            <div className="mt-4 max-w-3xl space-y-4 text-base leading-7 text-slate-700 dark:text-slate-300">
              <p>
                The job runs when the record is created and when the title or excerpt changes. It does not run on the
                HTML request, and attaching the file does not change those columns, so the callback does not enqueue
                itself again. Map <code>author</code> and <code>published_at</code> to your own schema.{" "}
                <code>create_blog_post_image</code> sets <code>template</code> to <code>blog_post</code>. Product,
                event, and the other helpers are on <Link href="/docs">/docs</Link>.
              </p>
              <p>
                <code>image_type: &quot;jpeg&quot;</code> is the documented claim for the delivered format. The JSON
                body does not include a content type, so the sample sets <code>image/jpeg</code> to match that claim.
                Attach only when <code>status</code> is <code>completed</code> and <code>url</code> is present. A 202
                means the render is still going. Leave the current attachment and retry the job. The processing response
                sets <code>Retry-After</code> to 30.
              </p>
              <p>
                <code>iat</code> is the record&apos;s <code>updated_at</code>. The docs say a present <code>iat</code>{" "}
                keeps the hosted cache for 24 hours from that timestamp. Omit it and the hosted cache does not expire on
                its own. Changing the title already changes the payload, so the next call is a different image either
                way. The API key stays in <code>OG_PILOT_API_KEY</code>. The browser never sees it.
              </p>
              <p>
                To draw the card yourself, keep the same model, job, and <code>attach</code> call. Replace the gem call
                with the bytes from your library or browser. This page does not name that library, and it does not
                publish how long that render takes or what it costs to run.
              </p>
            </div>
            <CodeSample label="Service, job, and invalidation" code={JOB_SAMPLE} />
          </section>

          <section className={sectionClass}>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">The same card from MCP</h2>
            <div className="mt-4 max-w-3xl space-y-4 text-base leading-7 text-slate-700 dark:text-slate-300">
              <p>
                An agent that already has the public page URL can call <code>generate_og_image</code> on the OG Pilot
                MCP server. The tool takes <code>url</code> and an optional <code>style</code>. It returns{" "}
                <code>image_url</code>. It does not take a Rails model, and it does not write Active Storage. Use the
                job above when the file has to be attached. Use MCP when the agent should produce a card for a URL that
                is already public.
              </p>
              <p>
                Auth is OAuth 2.1 with PKCE. The config file has no API key. <code>generate_og_image</code> needs a
                signed-in account with an active plan and a credit. A trial can call the API after the domain is
                verified. A trial cannot call <code>generate_og_image</code>. Install steps and the tool table are on{" "}
                <Link href="/docs/mcp" className={linkClass}>
                  /docs/mcp
                </Link>
                .
              </p>
            </div>
            <CodeSample label="MCP install" code={MCP_SAMPLE} />
          </section>

          <section className="mt-16 rounded-3xl bg-gradient-to-r from-slate-900 via-slate-800 to-orange-900 p-8 text-white shadow-xl sm:p-10">
            <h2 className="text-3xl font-bold">Get an API key</h2>
            <p className="mt-4 max-w-2xl text-sm leading-6 text-slate-200">
              Create an account, verify the domain, and call the API from the job. To let an agent generate a card for a
              public URL, install the MCP server on an active plan that has a credit.
            </p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
              <Button asChild size="lg" className="bg-white text-slate-900 hover:bg-slate-100">
                <Link href="/sign_up">
                  Get an API key
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-white/40 bg-transparent text-white hover:bg-white/10"
              >
                <Link href="/docs/mcp">Install the MCP server</Link>
              </Button>
            </div>
          </section>

          <ActivationGuide
            title="Related guides"
            links={[
              {
                href: "/rails-og-image-generator",
                label: "Rails setup",
                detail: "The short path: og_pilot_ruby returns a CDN URL for the layout.",
              },
              {
                href: "/og-image-api",
                label: "OG image API",
                detail: "Hosted API next to @vercel/og and a DIY Satori pipeline.",
              },
              {
                href: "/og-image-size",
                label: "og:image size",
                detail: "Sourced sizes for Facebook, LinkedIn, WhatsApp, and the rest.",
              },
              {
                href: "/docs/mcp",
                label: "MCP connector",
                detail: "Remote URL, OAuth, and generate_og_image.",
              },
              {
                href: "/open-graph-debugger",
                label: "Open Graph debugger",
                detail: "Check the tags on a public URL before you trust a share.",
              },
              {
                href: "/new",
                label: "Generate one image",
                detail: "One card in the browser, before you sign requests.",
              },
            ]}
          >
            <p>
              Sizes for the file you attach are on the{" "}
              <Link href="/og-image-size" className={linkClass}>
                og:image size cheat sheet
              </Link>
              . The{" "}
              <Link href="/og-image-api" className={linkClass}>
                OG image API comparison
              </Link>{" "}
              is the longer look at a hosted API, @vercel/og, and a DIY pipeline. After the tags are live, run the
              public URL through the{" "}
              <Link href="/open-graph-debugger" className={linkClass}>
                Open Graph debugger
              </Link>
              .
            </p>
          </ActivationGuide>

          <section className={sectionClass}>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">How to add a Rails OG image</h2>
            <ol className="mt-4 list-decimal space-y-2 pl-5 text-base leading-7 text-slate-700 dark:text-slate-300">
              <li>
                Pick a static file, your own renderer stored in Active Storage, or the OG Pilot API with the file cached
                on the record.
              </li>
              <li>Print og:title, og:description, og:image, og:url, and twitter:card from the layout.</li>
              <li>
                When the card is generated, attach it in a job and replace it when the record&apos;s card fields change.
              </li>
              <li>
                Create an API key and call OgPilotRuby from that job, or install MCP if an agent should generate a card
                for the public URL.
              </li>
              <li>Check the public URL in the Open Graph debugger.</li>
            </ol>
          </section>

          {faqs.length > 0 ? (
            <section className={sectionClass}>
              <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">Questions</h2>
              <div className="mt-6 space-y-6">
                {faqs.map((faq) => (
                  <article key={faq.question}>
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{faq.question}</h3>
                    <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">{faq.answer}</p>
                  </article>
                ))}
              </div>
            </section>
          ) : null}

          <SiteFooter className="mt-16" showSeparator={false} />
        </div>
      </div>
    </>
  );
}
